import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Container, Paper, Typography, Grid, Avatar, TextField, Button,
  List, ListItem, ListItemAvatar, ListItemText, Badge, Divider,
  CircularProgress, IconButton, InputAdornment
} from '@mui/material';
import { Send as SendIcon, Search as SearchIcon } from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import {
  getUserConversations, subscribeToMessages, sendMessage, markMessagesAsRead
} from '../../api/messages';

const Messages = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const unsubRef = useRef(null);

  useEffect(() => {
    loadConversations();
    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [user?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    if (!user?.id) return;
    try {
      const convs = await getUserConversations(user.id);
      setConversations(convs);
    } catch (err) {
      console.error('Error loading conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConv(conv);
    if (unsubRef.current) unsubRef.current();

    unsubRef.current = subscribeToMessages(conv.id, (msgs) => {
      setMessages(msgs);
    });

    markMessagesAsRead(conv.id, user.id);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    try {
      await sendMessage(
        selectedConv.id,
        user.id,
        user.displayName || `${user.firstName} ${user.lastName}`,
        newMessage.trim()
      );
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  const getOtherName = (conv) => {
    if (!conv.participantNames) return 'Unknown';
    const otherId = conv.participants?.find(id => id !== user.id);
    return conv.participantNames[otherId] || 'Unknown';
  };

  const filteredConversations = conversations.filter(conv =>
    getOtherName(conv).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ height: 'calc(100vh - 180px)', display: 'flex', overflow: 'hidden' }}>
      {/* Conversation List */}
      <Box sx={{ width: 320, borderRight: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Messages</Typography>
          <TextField
            size="small" fullWidth placeholder="Search conversations..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
          />
        </Box>
        <Divider />
        <List sx={{ overflow: 'auto', flex: 1 }}>
          {filteredConversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              No conversations yet
            </Typography>
          ) : (
            filteredConversations.map(conv => (
              <ListItem
                key={conv.id} button
                selected={selectedConv?.id === conv.id}
                onClick={() => selectConversation(conv)}
                sx={{
                  '&.Mui-selected': { bgcolor: '#E3F2FD' },
                  '&:hover': { bgcolor: '#f5f5f5' }
                }}
              >
                <ListItemAvatar>
                  <Avatar>{getOtherName(conv).charAt(0)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={getOtherName(conv)}
                  secondary={conv.lastMessage || 'No messages yet'}
                  primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                  secondaryTypographyProps={{ noWrap: true }}
                />
              </ListItem>
            ))
          )}
        </List>
      </Box>

      {/* Chat Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selectedConv ? (
          <>
            {/* Chat Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar>{getOtherName(selectedConv).charAt(0)}</Avatar>
              <Typography variant="h6" fontWeight={600}>{getOtherName(selectedConv)}</Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {messages.map(msg => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex', mb: 1.5,
                    justifyContent: msg.senderId === user.id ? 'flex-end' : 'flex-start'
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '70%', p: 1.5, borderRadius: 2,
                      bgcolor: msg.senderId === user.id ? '#2196F3' : '#f0f0f0',
                      color: msg.senderId === user.id ? 'white' : 'inherit'
                    }}
                  >
                    <Typography variant="body2">{msg.text}</Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}
                    >
                      {msg.createdAt?.toLocaleTimeString?.('en', { hour: '2-digit', minute: '2-digit' }) || ''}
                    </Typography>
                  </Box>
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 1 }}>
              <TextField
                fullWidth size="small" placeholder="Type a message..."
                value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              />
              <IconButton color="primary" onClick={handleSend} disabled={!newMessage.trim()}>
                <SendIcon />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <Typography variant="body1" color="text.secondary">
              Select a conversation to start messaging
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default Messages;
